import { Input } from "@legal-500/legal500-design-system";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { CmsImageUploadField } from "../../../components/CMS/CmsImageUploadField/CmsImageUploadField";
import {
  BackgroundField,
  BlockItemList,
  RichTextField,
  useSyncBlockContent,
  useUploadTracker,
} from "./blockEditorParts";
import type { IconListContent } from "./types";
import styles from "../CMS.module.css";

type IconListEditorProps = {
  value: IconListContent;
  onChange: (value: IconListContent) => void;
  onUploadingChange?: (uploading: boolean) => void;
};

export function IconListEditor({ value, onChange, onUploadingChange }: IconListEditorProps) {
  const { control, register, watch } = useForm<IconListContent>({ defaultValues: value });
  const { fields, append, remove, move } = useFieldArray({ control, name: "items" });
  const { uploading, report } = useUploadTracker(onUploadingChange);
  useSyncBlockContent(watch, value, onChange);

  return (
    <div className={styles.cardEditorFields}>
      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="icon-list-heading">
          Heading
        </label>
        <Input id="icon-list-heading" {...register("heading")} />
      </div>
      <RichTextField control={control} name="intro" id="icon-list-intro" label="Intro" />

      <BlockItemList
        noun="Item"
        keys={fields.map(field => field.id)}
        disabled={uploading}
        onMove={(from, to) => move(from, to)}
        onRemove={remove}
        onAdd={() => append({ iconUrl: "", heading: "", text: "" })}
        renderItem={(index, key) => (
          <>
            <div className={styles.cardEditorFieldRow}>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor={`icon-list-item-heading-${key}`}>
                  Heading <span className={styles.required}>*</span>
                </label>
                <Input
                  id={`icon-list-item-heading-${key}`}
                  required
                  pattern=".*\S.*"
                  title="Enter a heading"
                  {...register(`items.${index}.heading`)}
                />
              </div>
              <div className={styles.formField}>
                <Controller
                  name={`items.${index}.iconUrl`}
                  control={control}
                  render={({ field }) => (
                    <CmsImageUploadField
                      noun="icon"
                      recommendation="Recommended: a square PNG or WebP with a transparent background, 128 × 128 pixels or larger."
                      previewAlt="Item icon"
                      value={field.value}
                      cardLabel={`Item ${index + 1}: ${watch(`items.${index}.heading`) || "Untitled"}`}
                      onChange={field.onChange}
                      onUploadingChange={pending => report(key, pending)}
                    />
                  )}
                />
              </div>
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor={`icon-list-item-text-${key}`}>
                Text
              </label>
              <textarea
                id={`icon-list-item-text-${key}`}
                className={styles.textarea}
                rows={3}
                {...register(`items.${index}.text`)}
              />
            </div>
          </>
        )}
      />

      <BackgroundField register={register} name="background" stored={value.background} />
    </div>
  );
}
